import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { X, Save, Lock, User as UserIcon, Eye, EyeOff } from 'lucide-react';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose }) => {
  const { user: currentUser, refreshUserData } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [profileData, setProfileData] = useState({
    email: currentUser?.email || '',
    full_name: currentUser?.full_name || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  React.useEffect(() => {
    if (isOpen && currentUser) {
      setProfileData({
        email: currentUser.email || '',
        full_name: currentUser.full_name || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  }, [isOpen, currentUser]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!profileData.currentPassword) {
      alert(t('profile.enterCurrentPassword'));
      return;
    }

    if (profileData.newPassword && profileData.newPassword !== profileData.confirmPassword) {
      alert(t('profile.passwordsNoMatch'));
      return;
    }

    if (profileData.newPassword && profileData.newPassword.length < 8) {
      alert(t('profile.passwordTooShort'));
      return;
    }

    try {
      setLoading(true);

      const authToken = localStorage.getItem('auth_token');
      if (!authToken) {
        alert(t('profile.sessionExpired'));
        return;
      }

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('password_hash')
        .eq('id', currentUser?.id)
        .maybeSingle();

      if (userError || !userData) {
        alert(t('profile.verifyFailed'));
        return;
      }

      const bcrypt = (await import('bcryptjs')).default;
      const isPasswordValid = await bcrypt.compare(profileData.currentPassword, userData.password_hash);

      if (!isPasswordValid) {
        alert(t('profile.incorrectPassword'));
        return;
      }

      let newPasswordHash = null;
      if (profileData.newPassword) {
        newPasswordHash = await bcrypt.hash(profileData.newPassword, 10);
      }

      const { data, error } = await supabase.rpc('update_profile_with_jwt', {
        p_user_id: currentUser?.id,
        p_full_name: profileData.full_name !== currentUser?.full_name ? profileData.full_name : null,
        p_email: profileData.email !== currentUser?.email ? profileData.email : null,
        p_password_hash: newPasswordHash,
        p_avatar_url: null
      });

      if (error) {
        alert(`Failed to update profile: ${error.message}`);
        return;
      }

      if (!data?.success) {
        alert(data?.error || t('profile.updateFailed'));
        return;
      }

      alert(t('profile.updateSuccess'));
      await refreshUserData();
      onClose();
    } catch (err: any) {
      alert(t('profile.updateFailed') + ': ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <UserIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {t('profile.title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleUpdateProfile} className="p-6">
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {t('profile.infoText')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('profile.fullName')}
                </label>
                <Input
                  type="text"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                  placeholder={t('profile.yourFullName')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('users.email')}
                </label>
                <Input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Lock className="h-5 w-5" />
                {t('profile.changePassword')}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('profile.newPassword')}
                  </label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={profileData.newPassword}
                      onChange={(e) => setProfileData({ ...profileData, newPassword: e.target.value })}
                      placeholder={t('profile.keepCurrent')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('profile.confirmPassword')}
                  </label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      value={profileData.confirmPassword}
                      onChange={(e) => setProfileData({ ...profileData, confirmPassword: e.target.value })}
                      placeholder={t('profile.confirmPlaceholder')}
                      disabled={!profileData.newPassword}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      disabled={!profileData.newPassword}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <label className="block text-sm font-medium text-red-700 dark:text-red-400 mb-2">
                {t('profile.currentPassword')} *
              </label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={profileData.currentPassword}
                  onChange={(e) => setProfileData({ ...profileData, currentPassword: e.target.value })}
                  placeholder={t('profile.currentPlaceholder')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('profile.mustEnterCurrent')}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              {t('action.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {loading ? t('message.loading') : t('profile.saveChanges')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
